/**
 * 인증 Context
 *
 * 책임:
 * - 토큰 + 사용자 정보를 전역 상태로 관리
 * - localStorage에 토큰 저장 (자동 로그인 지원)
 * - 401 감지 시 자동 로그아웃
 * - useAuth 훅에서 사용
 */

import { createContext, type ReactNode, useEffect, useState } from 'react';

// 사용자 정보 인터페이스
export interface User {
  id: string;
  name: string;
}

// Context에 포함된 상태 + 액션
export interface AuthContextType {
  token: string | null;
  user: User | null;
  setAuth: (token: string, user: User) => void;
  logout: () => void;
}

// Context 객체 생성
export const AuthContext = createContext<AuthContextType | null>(null);

// Context Provider 컴포넌트
export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // 초기 로드: localStorage에서 저장된 토큰 복원 (자동 로그인)
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        // 저장된 user JSON 파싱 실패 → 무시하고 로그인 화면 표시
      }
    }
  }, []);

  // 401 자동 로그아웃 리스너
  // client.ts에서 발행하는 'auth:logout' 이벤트를 listen
  useEffect(() => {
    function handleLogout() {
      handleLogoutInternal();
    }
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // 로그인: 토큰 + 사용자 정보를 저장하고 상태 업데이트
  function handleSetAuth(newToken: string, newUser: User) {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }

  // 로그아웃: localStorage 제거 + 상태 초기화
  function handleLogoutInternal() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }

  // Context 값
  const value: AuthContextType = {
    token,
    user,
    setAuth: handleSetAuth,
    logout: handleLogoutInternal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
